import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { PageSkeleton } from '@/components/shared/page-skeleton'
import { SectionCard } from '@/components/shared/section-card'
import { ContextualSaveBar } from '@/components/shared/contextual-save-bar'
import { IgConnectionCard } from '@/components/social-listening/ig-connection-card'
import { MonitoringSection } from '@/components/social-listening/monitoring-section'
import { EventsTimeline } from '@/components/settings/events-timeline'
import { useIgStatus, useListeningConfig, useUpsertConfig } from '@/hooks/use-social-listening'

export const Route = createFileRoute('/_app/settings/social-listening')({
  component: SocialListeningSettings,
})

function SocialListeningSettings() {
  const { data: status, isLoading: statusLoading } = useIgStatus()
  const { data: config, isLoading: configLoading } = useListeningConfig()
  const upsertConfig = useUpsertConfig()

  const [hashtags, setHashtags] = useState<string[]>([])
  const [syncInterval, setSyncInterval] = useState('12')
  const [moderationEnabled, setModerationEnabled] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Sync state from server once loaded
  if (config && !initialized) {
    setHashtags(config.hashtagsToTrack || [])
    setSyncInterval(String(config.syncIntervalHours || 12))
    setModerationEnabled(config.commentModerationEnabled ?? false)
    setInitialized(true)
  }

  const isLoading = statusLoading || configLoading

  if (isLoading) {
    return <PageSkeleton sections={2} />
  }

  const isConnected = status?.connected

  const hasChanges =
    initialized &&
    (JSON.stringify(hashtags) !== JSON.stringify(config?.hashtagsToTrack || []) ||
      syncInterval !== String(config?.syncIntervalHours || 12) ||
      moderationEnabled !== (config?.commentModerationEnabled ?? false))

  const handleSave = async () => {
    try {
      await upsertConfig.mutateAsync({
        hashtagsToTrack: hashtags,
        syncIntervalHours: Number(syncInterval),
        isActive: true,
        commentModerationEnabled: moderationEnabled,
      })
      toast.success('Configuração salva')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar'
      toast.error(message)
    }
  }

  const handleDiscard = () => {
    if (config) {
      setHashtags(config.hashtagsToTrack || [])
      setSyncInterval(String(config.syncIntervalHours || 12))
      setModerationEnabled(config.commentModerationEnabled ?? false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        size="sm"
        title="Social Listening"
        description="Monitore menções e interações da marca no Instagram."
      />

      <IgConnectionCard />

      {isConnected && (
        <>
          <ContextualSaveBar
            isDirty={hasChanges}
            isSaving={upsertConfig.isPending}
            onSave={handleSave}
            onDiscard={handleDiscard}
          />

          <MonitoringSection
            isDirty={hasChanges}
            hashtags={hashtags}
            setHashtags={setHashtags}
            syncInterval={syncInterval}
            setSyncInterval={setSyncInterval}
            moderationEnabled={moderationEnabled}
            setModerationEnabled={setModerationEnabled}
          />

          <SectionCard title="Últimos eventos" description="Webhooks e menções recentes.">
            <EventsTimeline
              filter={{ platform: 'instagram', direction: 'in' }}
              limit={10}
              showFilters={false}
            />
          </SectionCard>
        </>
      )}
    </div>
  )
}
